import type { AdvisorRegion } from './types';

export type SchematicSlotId =
  | 'oporto'
  | 'centro'
  | 'lisboa'
  | 'alentejo'
  | 'algarve';

export type SchematicSlot = {
  id: SchematicSlotId;
  label: string;
  x: number;
  y: number;
};

export const MAINLAND_SLOTS: readonly SchematicSlot[] = [
  { id: 'oporto', label: 'Oporto', x: 38, y: 16 },
  { id: 'centro', label: 'Centro', x: 36, y: 36 },
  { id: 'lisboa', label: 'Lisboa', x: 28, y: 52 },
  { id: 'alentejo', label: 'Alentejo', x: 36, y: 68 },
  { id: 'algarve', label: 'Algarve', x: 38, y: 86 },
];

export const OUTLIER_SLOT_IDS = ['madeira', 'azores'] as const;
export type OutlierSlotId = (typeof OUTLIER_SLOT_IDS)[number];

const SLOT_ALIASES: Record<SchematicSlotId | OutlierSlotId, string[]> = {
  oporto: ['oporto', 'porto', 'north'],
  centro: ['centro', 'center', 'centre', 'coimbra'],
  lisboa: ['lisboa', 'lisbon'],
  alentejo: ['alentejo', 'comporta'],
  algarve: ['algarve'],
  madeira: ['madeira'],
  azores: ['azores', 'açores', 'acores'],
};

const normalize = (value: string): string =>
  value.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();

const titleCaseWord = (word: string): string => {
  const letters = word.replace(/[^\p{L}]/gu, '');
  if (!letters) return word;
  const isUniformCase =
    letters === letters.toUpperCase() || letters === letters.toLowerCase();
  if (!isUniformCase) return word;
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
};

// CMS custom-region titles are often stored in shouting caps (`OPORTO`,
// `CASTILLA- LEON & ASTURIAS`). Title-case uniform words; keep mixed-case
// words (`Madeira`, `Azores`) as authored.
export const titleCaseRegionTitle = (title: string): string =>
  title.replace(/\S+/g, titleCaseWord);

/**
 * A region as XT names it, tidied for display: "LISBON  &  SURROUNDINGS" →
 * "Lisbon & Surroundings" (the XT chapter title on send is the same).
 */
export const regionName = (title: string): string =>
  titleCaseRegionTitle(title.replace(/\s+/g, ' ').trim());

/** The region's first name ("Oporto"), for matching titles to cities. */
export const displayRegionTitle = (title: string): string => {
  const short = title.split(/[&/]| and /i)[0]?.trim() || title;
  return titleCaseRegionTitle(short);
};

export const matchRegionSlot = (
  title: string
): SchematicSlotId | OutlierSlotId | null => {
  const haystack = normalize(title);
  for (const [slot, aliases] of Object.entries(SLOT_ALIASES) as [
    SchematicSlotId | OutlierSlotId,
    string[],
  ][]) {
    if (aliases.some((alias) => haystack.includes(alias))) return slot;
  }
  return null;
};

/** Merge/Add key: Porto and Oporto & North are the same leg. */
export const advisorRegionKey = (title: string): string =>
  matchRegionSlot(title) ?? displayRegionTitle(title).trim().toLowerCase();

export const isOutlierRegion = (region: AdvisorRegion): boolean => {
  const slot = matchRegionSlot(region.title);
  return slot === 'madeira' || slot === 'azores';
};

export const mainlandRegions = (regions: AdvisorRegion[]): AdvisorRegion[] =>
  regions.filter((region) => !isOutlierRegion(region));

export const outlierRegions = (regions: AdvisorRegion[]): AdvisorRegion[] =>
  regions.filter(isOutlierRegion);

export const findRegionForSlot = (
  regions: AdvisorRegion[],
  slotId: SchematicSlotId | OutlierSlotId
): AdvisorRegion | undefined =>
  regions.find((region) => matchRegionSlot(region.title) === slotId);

/** Destination "Madeira" → CMS chip "Madeira Island". */
export const matchRegionFromBrief = (
  destination: string | null | undefined,
  regions: readonly AdvisorRegion[]
): AdvisorRegion | null => {
  const dest = destination?.trim();
  if (!dest || regions.length === 0) return null;
  const slot = matchRegionSlot(dest);
  if (slot) {
    const bySlot = findRegionForSlot([...regions], slot);
    if (bySlot) return bySlot;
  }
  const hay = normalize(dest);
  return (
    regions.find((region) => {
      const title = normalize(region.title);
      const short = normalize(displayRegionTitle(region.title));
      return title.includes(hay) || hay.includes(short) || short.includes(hay);
    }) ?? null
  );
};
